import { Controller } from "@hotwired/stimulus"

// Scroll-reveal for sales-surface sections. Wrap a section with
// data-controller="reveal" and mark the element(s) that should animate in
// with data-reveal (the controller's own element counts if it carries the
// attribute itself). Each element fades/translates in once it crosses ~15%
// visibility, then stops being observed. data-reveal-delay="N" staggers the
// reveal using the existing .stagger-N transition-delay classes. Respects
// prefers-reduced-motion by revealing everything immediately with no
// IntersectionObserver at all.
//
// Controller-armed pattern: sales.css only hides [data-reveal] elements
// when an ancestor carries `.reveal-armed` (see components/sales.css). This
// controller is the only thing that ever adds that class, and only after
// confirming there's something to reveal — so with JS disabled, or in the
// instant before it boots, [data-reveal] content is never hidden. Once
// armed there's a brief first-paint window before the IntersectionObserver
// resolves visibility, which is expected and matches normal reveal timing.
export default class extends Controller {
  connect() {
    this.revealTargets = this.element.matches("[data-reveal]")
      ? [this.element]
      : Array.from(this.element.querySelectorAll("[data-reveal]"))

    if (this.revealTargets.length === 0) return

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // Don't arm — reduced-motion users must never see a hidden state,
      // even momentarily. Reveal immediately with no observer.
      this.revealTargets.forEach((el) => el.classList.add("revealed"))
      return
    }

    this.element.classList.add("reveal-armed")

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return

          const target = entry.target
          const delay = target.dataset.revealDelay
          if (delay) target.classList.add(`stagger-${delay}`)
          target.classList.add("revealed")

          observer.unobserve(target)
        })
      },
      { threshold: 0.15 }
    )

    this.revealTargets.forEach((el) => this.observer.observe(el))
  }

  disconnect() {
    this.observer?.disconnect()
  }
}
